/** Filter state + sessionStorage persistence for a Licenses-style page, keyed by a caller-chosen
 * storage key so pages/Licenses.tsx (all services) and each ServiceLicensesTab (one service)
 * don't clobber each other's filters. Reloads from storage whenever the key itself changes —
 * React Router keeps the same component instance across a :serviceId param change, so a plain
 * lazy useState initializer alone wouldn't reload for the new service. */
import { useEffect, useState } from 'react'
import { DEFAULT_FILTERS } from './personRows'
import type { StoredFilters } from './personRows'

function load(storageKey: string): StoredFilters {
  try {
    const raw = sessionStorage.getItem(storageKey)
    return raw ? { ...DEFAULT_FILTERS, ...JSON.parse(raw) } : DEFAULT_FILTERS
  } catch {
    return DEFAULT_FILTERS
  }
}

export function useLicenseFilters(storageKey: string) {
  const [filters, setFilters] = useState<StoredFilters>(() => load(storageKey))

  useEffect(() => {
    setFilters(load(storageKey))
  }, [storageKey])

  useEffect(() => {
    sessionStorage.setItem(storageKey, JSON.stringify(filters))
  }, [filters, storageKey])

  function updateFilters(patch: Partial<StoredFilters>) {
    setFilters(prev => ({ ...prev, ...patch }))
  }

  return { filters, updateFilters }
}
